import apiClient from './apiClient';

// Экспортируем apiClient для обратной совместимости  
export { apiClient };

// Определяем типы для данных, если они еще не определены
// Например:
export interface Account {
  id: number;
  account_number: string;
  currency: string;
  balance: string;
  status: string;
  account_type: string;
  name: string;
  created_at: string;
  is_demo?: boolean;
}

export interface AccountTariff {
  id: number;
  account_id: number;
  tariff_id: number;
  is_active: boolean;
  tariff: {
    id: number;
    name: string;
    description: string;
    tariff_type: string;
    fee_type: string;
    fee_percentage?: number;
    fee_fixed?: number;
    currency_from: string;
    direction: string;
  };
}

export interface AccountLimit {
  id: number;
  account_id: number;
  limit_id: number;
  is_active: boolean;
  current_usage?: number;
  usage_count?: number;
  limit: {
    id: number;
    name: string;
    description: string;
    limit_type: string;
    category: string;
    min_amount?: number;
    max_amount?: number;
    currency: string;
    identification_method?: string;
  };
}

export interface AccountCreationData {
  account_type: string;
  currency: string;
  name?: string;
  description?: string;
  is_demo?: boolean;
}

// Методы для работы с API
const api = {
  // Auth endpoints
  login: (credentials: { username: string; password: string }) => {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    
    return apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  
  loginWith2FA: (credentials: { email: string; password: string; code: string }) => {
    return apiClient.post('/auth/login-2fa', credentials);
  },
  
  register: (userData: any) => 
    apiClient.post('/auth/register', userData),
  
  getCurrentUser: () => 
    apiClient.get('/auth/me'),
  
  refreshToken: (bearer: string) =>
    apiClient.post('/auth/refresh', null, {
      headers: { Authorization: `Bearer ${bearer}` },
      _skipAuthRefresh: true,
    } as any),
  
  resetPassword: (token: string, new_password: string) =>
    apiClient.post('/auth/reset-password', { token, new_password }),
  
  verifyEmail: (token: string) => 
    apiClient.post('/auth/verify-email', { token }),

  resendVerification: (email: string) => 
    apiClient.post('/auth/resend-verification', { email }),

  resendVerificationAuthenticated: () => 
    apiClient.post('/auth/resend-verification-authenticated'),

  forgotPassword: (email: string) => 
    apiClient.post('/auth/forgot-password', { email }),

  changePassword: (current_password: string, new_password: string) =>
    apiClient.post('/auth/change-password', { current_password, new_password }),

  requestPhoneChange: (new_phone: string) =>
    apiClient.post('/auth/phone/request-change', { new_phone }),

  confirmPhoneChange: (code: string) =>
    apiClient.post('/auth/phone/confirm-change', { code }),

  // Account endpoints
  getAccounts: () => apiClient.get<Account[]>('/accounts/'),
  getAccountById: (id: string) => apiClient.get<Account>(`/accounts/${id}`),
  createAccount: (data: AccountCreationData) => apiClient.post<Account>('/accounts/', data),
  updateAccount: (id: string, data: { name: string; status: string }) => apiClient.put<Account>(`/accounts/${id}`, data),
  deleteAccount: (id: string) => apiClient.delete(`/accounts/${id}`),
  getDemoAccounts: () => apiClient.get<Account[]>('/accounts/demo'),
  createDemoAccount: () => apiClient.post<Account>('/accounts/demo/create'),
  
  // Account tariffs and limits
  getAccountTariffs: (accountId: string) => apiClient.get(`/accounts/${accountId}/tariffs`),
  getAccountLimits: (accountId: string) => apiClient.get(`/accounts/${accountId}/limits`),
  getPreviewTariffsLimits: (accountType: string, currency: string) => 
    apiClient.get('/accounts/preview-tariffs-limits', { 
      params: { account_type: accountType, currency } 
    }),
  
  // Transaction endpoints
  getTransactions: (params?: any) => apiClient.get('/transactions/', { params }),
  exportTransactions: (params?: any) =>
    apiClient.get('/transactions/export', { params, responseType: 'blob' }),
  createTransaction: (data: any) => apiClient.post('/transactions/', data),
  createTransfer: (data: any) => apiClient.post('/transactions/transfer', data),
  getOperations: (params?: any) => apiClient.get('/transactions/operations', { params }),
  
  // Payment endpoints
  getPayments: (params?: any) => apiClient.get('/payments/', { params }),
  createPayment: (data: any) => apiClient.post('/payments/', data),
  createInvoice: (data: any) => apiClient.post('/payments/invoice', data),
  
  // Report endpoints
  getReports: (params?: any) => apiClient.get('/reports/', { params }),
  
  // Dashboard endpoints
  getDashboardStats: () => apiClient.get('/dashboard/'),
  
  // Limits endpoints
  getMyLimits: () => apiClient.get('/limits/my/limits'),

  // Эффективные регуляторные лимиты клиента (по уровню верификации) с usage
  getEffectiveLimits: (currency = 'KGS') =>
    apiClient.get('/limits/my/effective', { params: { currency } }),

  // Тарифы
  getTariffPlans: () => apiClient.get('/tariffs/'),
  getBasicTariffsGrouped: () => apiClient.get('/tariffs/basic/grouped'),
  getBasicLimitsGrouped: () => apiClient.get('/tariffs/basic/limits-grouped'),
  getMyAccountsTariffsLimits: () => apiClient.get('/tariffs/my/accounts-tariffs-limits'),
  getMyTariffPlan: () => apiClient.get('/tariffs/my/plan'),
  calculateMyFee: (params: {
    amount: number
    currency?: string
    transaction_type?: string
    account_id?: number | null
    direction?: string | null
  }) => {
    const query: Record<string, string | number> = {
      amount: params.amount,
      transaction_type: (params.transaction_type || 'transfer').toLowerCase(),
    }
    if (params.currency) query.currency = params.currency
    if (params.account_id != null) query.account_id = params.account_id
    if (params.direction) query.direction = params.direction
    return apiClient.post('/tariffs/my/calculate', null, { params: query })
  },

  // Users endpoints
  checkRecipient: (data: { identifier: string }) => apiClient.post('/users/check-recipient', data),
  updateUserLanguage: (language: string) => apiClient.put('/users/me/language', { language }),
  updateUserIdentity: (data: { passport_series?: string; passport_number?: string; tax_id?: string }) =>
    apiClient.put('/users/me/identity', data),

  // 2FA endpoints
  disable2FA: (code: string) => apiClient.post('/auth/setup-2fa', { enable: false, code }),
  setup2FA: (enable = true) => apiClient.post('/auth/setup-2fa', { enable }),
  verify2FA: (code: string) => apiClient.post('/auth/verify-2fa', { code }),
  updateNotifications: (data: any) => apiClient.put('/users/me', data),

  // Notification settings endpoints
  getNotificationSettings: () => apiClient.get('/auth/notification-settings'),
  updateNotificationSettings: (data: any) => apiClient.put('/auth/notification-settings', data),

  // Sessions endpoints
  getSessions: (params?: { sessions_limit?: number; history_limit?: number }) => 
    apiClient.get('/sessions/', { params }),
  getActiveSessions: (limit?: number) => 
    apiClient.get('/sessions/active', { params: { limit } }),
  getLoginHistory: (limit?: number) => 
    apiClient.get('/sessions/history', { params: { limit } }),
  deactivateSession: (sessionId: string) => 
    apiClient.post('/sessions/deactivate', { session_id: sessionId }),
  deactivateAllSessions: (excludeCurrent = true) => 
    apiClient.post('/sessions/deactivate-all', null, { params: { exclude_current: excludeCurrent } }),
  getCurrentSession: () => apiClient.get('/sessions/current'),

  // Team management endpoints
  getTeamMembers: (params?: { 
    skip?: number; 
    limit?: number; 
    query?: string; 
    role?: string; 
    status?: string; 
    sort_by?: string; 
    sort_order?: string; 
  }) => apiClient.get('/team/', { params }),
  
  getTeamMember: (memberId: number) => apiClient.get(`/team/${memberId}`),
  
  createTeamMember: (data: {
    email: string;
    role: string;
    first_name?: string;
    last_name?: string;
    description?: string;
    send_invitation?: boolean;
    custom_message?: string;
  }) => apiClient.post('/team/', data),
  
  updateTeamMember: (memberId: number, data: {
    role?: string;
    status?: string;
    first_name?: string;
    last_name?: string;
    description?: string;
  }) => apiClient.put(`/team/${memberId}`, data),
  
  deleteTeamMember: (memberId: number) => apiClient.delete(`/team/${memberId}`),
  
  resendInvitation: (memberId: number) => apiClient.post(`/team/${memberId}/resend-invitation`),
  
  getTeamStatistics: () => apiClient.get('/team/statistics'),
  
  acceptInvitation: (data: { invitation_token: string; first_name?: string; last_name?: string }) => 
    apiClient.post('/team/accept-invitation', data),
  
  bulkTeamMemberAction: (data: {
    member_ids: number[];
    action: string;
    reason?: string;
  }) => apiClient.post('/team/bulk-action', data),

  // Currencies
  getSupportedCurrencies: () => apiClient.get('/currencies/supported'),

  // API Logs endpoints
  getApiLogs: (params?: {
    limit?: number;
    offset?: number;
    operation_type?: string;
  }) => apiClient.get('/transactions/operations', { params }),

  // Webhook Logs endpoints
  getWebhookDeliveries: (params?: {
    page?: number;
    size?: number;
    subscription_id?: number;
    status?: string;
    event_type?: string;
  }) => apiClient.get('/webhooks/deliveries', { params }),

  getWebhookDeliveryDetail: (deliveryId: number) =>
    apiClient.get(`/webhooks/deliveries/${deliveryId}`),

  retryWebhookDelivery: (deliveryId: number) =>
    apiClient.post(`/webhooks/deliveries/${deliveryId}/retry`),

  cancelWebhookDelivery: (deliveryId: number) =>
    apiClient.post(`/webhooks/deliveries/${deliveryId}/cancel`),

  submitFraudReport: (data: Record<string, unknown>) =>
    apiClient.post('/fraud-reports/', data),

  getFraudReportStatus: (caseId: number) =>
    apiClient.get(`/fraud-reports/${caseId}`),
};

export default api; 